import { ChartBrush, SingleAreaBrushPanorama } from '@/components/charts/chartBrush'
import { ChartContainer } from '@/components/charts/chartContainer'
import { ChartTooltip } from '@/components/charts/chartTooltip'
import { ChartWatermark } from '@/components/charts/chartWatermark'
import { Loader } from '@/components/common/loader'
import { cn } from '@/utils/cn'
import type { BorrowMarketHistoricalDataPoint } from '@/utils/internalAppSdk'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { formatDate, formatDecimalToPercentage, formatUSD } from '@vaultsfyi/common/utils'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { BORROW_MARKET_DETAILS_STAT_VALUES, type BorrowMarketDetailsStat } from './borrowMarketDetailsStats.constants'

interface BorrowMarketDetailsStatsChartProps {
  data: BorrowMarketHistoricalDataPoint[]
  stat: BorrowMarketDetailsStat
  className?: string
  isError?: boolean
  isLoading?: boolean
  isUnsupported?: boolean
  showBrush?: boolean
}

export const BorrowMarketDetailsStatsChart = ({
  data,
  stat,
  className,
  isError = false,
  isLoading = false,
  isUnsupported = false,
  showBrush = false,
}: BorrowMarketDetailsStatsChartProps) => {
  const series = getChartSeries(stat)
  const chartData = getChartData(data, series)
  const hasLegend = series.length > 1

  if (isUnsupported) {
    return (
      <BorrowMarketDetailsStatsChartState className={className}>
        Historical data is not available for this asset.
      </BorrowMarketDetailsStatsChartState>
    )
  }

  if (isError) {
    return (
      <BorrowMarketDetailsStatsChartState className={className}>
        Failed to load historical data.
      </BorrowMarketDetailsStatsChartState>
    )
  }

  if (chartData.length === 0) {
    if (isLoading) {
      return (
        <ChartContainer className={className}>
          <Loader />
        </ChartContainer>
      )
    }

    return (
      <BorrowMarketDetailsStatsChartState className={className}>
        No historical data for this time range.
      </BorrowMarketDetailsStatsChartState>
    )
  }

  return (
    <div>
      <ChartContainer className={className}>
        {showBrush && hasLegend && (
          <BorrowMarketDetailsStatsChartLegend
            series={series}
            className="pointer-events-none absolute right-0 bottom-[68px] z-[1]"
          />
        )}
        <ResponsiveContainer>
          <AreaChart data={chartData}>
            <defs>
              {series.map(({ key, color }) => (
                <linearGradient
                  key={key}
                  id={`borrowMarketDetailsStatsFill-${key}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="100%"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor={color} stopOpacity={1} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                </linearGradient>
              ))}
            </defs>
            <XAxis
              dataKey="timestamp"
              tickLine={false}
              tickMargin={12}
              type="number"
              interval="preserveStartEnd"
              domain={['dataMin', 'dataMax']}
              tickCount={7}
              tickFormatter={(timestamp) => formatDate({ timestamp })}
            />
            <YAxis
              tickMargin={0}
              tickLine={false}
              tickFormatter={(value) => formatYAxisValue(Number(value), stat)}
              interval="preserveStartEnd"
            />
            <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgb(var(--border))" />
            <Tooltip
              content={({ payload }) => <BorrowMarketDetailsStatsChartTooltip payload={payload} series={series} />}
            />
            {series.map(({ key, areaKey, color, stackId }) => (
              <Area
                key={key}
                dataKey={areaKey ?? key}
                type="monotone"
                isAnimationActive={false}
                stroke={color}
                strokeOpacity={1}
                strokeWidth={2}
                fill={`url(#borrowMarketDetailsStatsFill-${key})`}
                stackId={stackId}
                connectNulls
              />
            ))}
            <ChartWatermark />
            {showBrush && (
              <ChartBrush data={chartData}>
                <SingleAreaBrushPanorama
                  color={series[0].color}
                  dataKey={series[0].key}
                  gradientId={`borrowMarketDetailsStatsBrushFill-${series[0].key}`}
                />
              </ChartBrush>
            )}
          </AreaChart>
        </ResponsiveContainer>
      </ChartContainer>
      {!showBrush && hasLegend && <BorrowMarketDetailsStatsChartLegend series={series} />}
    </div>
  )
}

const BorrowMarketDetailsStatsChartState = ({
  children,
  className,
}: {
  children: string
  className?: string
}) => (
  <ChartContainer className={className}>
    <p className="text-center text-body-foreground text-sm">{children}</p>
  </ChartContainer>
)

const BorrowMarketDetailsStatsChartLegend = ({ series, className }: { series: ChartSeries[]; className?: string }) => (
  <ul className={cn('mt-2 flex flex-wrap justify-end gap-x-4 gap-y-1', className)}>
    {series.map(({ key, label, color }) => (
      <li key={key} className="flex items-center gap-2 text-body-foreground text-sm">
        <span className="size-3 min-w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />
        <span>{label}</span>
      </li>
    ))}
  </ul>
)

const BorrowMarketDetailsStatsChartTooltip = ({
  payload,
  series,
}: {
  payload:
    | Array<{
        dataKey?: unknown
        value?: unknown
        payload?: BorrowMarketDetailsStatsChartPoint
      }>
    | undefined
  series: ChartSeries[]
}) => {
  const data = payload?.[0]?.payload
  if (!data || !payload) {
    return null
  }

  return (
    <ChartTooltip>
      <span>{formatDate({ timestamp: data.timestamp, showYear: true })}</span>
      <div className="flex flex-col gap-1">
        {payload.map(({ dataKey }) => {
          const item = series.find((seriesItem) => seriesItem.key === dataKey || seriesItem.areaKey === dataKey)
          const rawValue = item ? data[item.key] : undefined
          const numericValue = typeof rawValue === 'number' ? rawValue : undefined
          if (!item || numericValue === undefined || !Number.isFinite(numericValue)) return null

          return (
            <p key={item.key} className="flex items-center gap-1">
              <span className="size-2 rounded-full" style={{ backgroundColor: item.color }} />
              <span>{item.label}:</span>
              <span className="font-semibold">{formatSeriesValue(numericValue, item)}</span>
            </p>
          )
        })}
      </div>
    </ChartTooltip>
  )
}

type ChartSeriesKey = 'borrowRate' | 'supplyRate' | 'utilizationRate' | 'totalSuppliedUsd' | 'totalBorrowedUsd'
type ChartSeriesAreaKey = ChartSeriesKey | 'borrowRateSpread' | 'totalSuppliedUsdSpread'
type ChartSeriesType = 'rate' | 'usd'

interface ChartSeries {
  key: ChartSeriesKey
  areaKey?: ChartSeriesAreaKey
  label: string
  color: string
  type: ChartSeriesType
  fractionDigits?: number
  stackId?: string
}

interface BorrowMarketDetailsStatsChartPoint {
  timestamp: number
  borrowRate: number
  borrowRateSpread: number
  supplyRate: number
  utilizationRate: number
  totalSuppliedUsd: number
  totalSuppliedUsdSpread: number
  totalBorrowedUsd: number
}

const getChartSeries = (stat: BorrowMarketDetailsStat): ChartSeries[] => {
  switch (stat) {
    case BORROW_MARKET_DETAILS_STAT_VALUES.borrowApy:
      return [
        {
          key: 'supplyRate',
          label: 'Supply APY',
          color: 'rgb(var(--chart-green))',
          type: 'rate',
          stackId: 'borrowApy',
        },
        {
          key: 'borrowRate',
          areaKey: 'borrowRateSpread',
          label: 'Borrow APY',
          color: 'rgb(var(--chart-red))',
          type: 'rate',
          stackId: 'borrowApy',
        },
      ]
    case BORROW_MARKET_DETAILS_STAT_VALUES.utilization:
      return [
        {
          key: 'utilizationRate',
          label: 'Utilization',
          color: 'rgb(var(--chart-yellow))',
          type: 'rate',
        },
      ]
    case BORROW_MARKET_DETAILS_STAT_VALUES.totalSupplied:
      return [
        {
          key: 'totalBorrowedUsd',
          label: 'Total borrowed',
          color: 'rgb(var(--chart-violet))',
          type: 'usd',
          stackId: 'totalSupplied',
        },
        {
          key: 'totalSuppliedUsd',
          areaKey: 'totalSuppliedUsdSpread',
          label: 'Total supplied',
          color: 'rgb(var(--chart-blue))',
          type: 'usd',
          stackId: 'totalSupplied',
        },
      ]
  }
}

const getChartData = (
  data: BorrowMarketHistoricalDataPoint[],
  series: ChartSeries[]
): BorrowMarketDetailsStatsChartPoint[] =>
  data
    .map((point) => ({
      timestamp: point.timestamp * SECOND_IN_MILLISECONDS,
      borrowRate: point.borrowRate,
      borrowRateSpread: Math.max(point.borrowRate - point.supplyRate, 0),
      supplyRate: point.supplyRate,
      utilizationRate: point.utilizationRate,
      totalSuppliedUsd: Number(point.totalSupplied.usd ?? 0),
      totalSuppliedUsdSpread: Math.max(Number(point.totalSupplied.usd ?? 0) - Number(point.totalBorrowed.usd ?? 0), 0),
      totalBorrowedUsd: Number(point.totalBorrowed.usd ?? 0),
    }))
    .filter((point) => series.some(({ key }) => Number.isFinite(point[key])))

const formatYAxisValue = (value: number, stat: BorrowMarketDetailsStat) => {
  switch (stat) {
    case BORROW_MARKET_DETAILS_STAT_VALUES.borrowApy:
    case BORROW_MARKET_DETAILS_STAT_VALUES.utilization:
      return formatDecimalToPercentage(value, { fractionDigits: 0 })
    case BORROW_MARKET_DETAILS_STAT_VALUES.totalSupplied:
      return formatUSD(value, { minimumFractionDigits: 0 })
  }
}

const formatSeriesValue = (value: number, series: Pick<ChartSeries, 'type' | 'fractionDigits'>) =>
  series.type === 'rate'
    ? formatDecimalToPercentage(value, { fractionDigits: series.fractionDigits ?? 0 })
    : formatUSD(value, { minimumFractionDigits: 0 })
